import { useState } from 'react'

import {
  Banner,
  Dialog,
  DragDropFile,
  DragDropFileProvider,
  ErrorMessage,
  type FileWithStatus,
  Layout,
  Radio,
  Text,
} from '@loadsmart/loadsmart-ui'

import { type DistanceUnit } from '../domain/DistanceUnit'
import { getRateType, type RateType } from '../domain/RateType'
import { formatDistanceLabel } from '../presenters/rfpPresenter'
import { RadioButtonGroup } from './UploadRfpLanesDialog.styles'

export type UploadRfpLanesDialogType = {
  distanceUnit: DistanceUnit
  rateType: RateType
  onUploadConfirm: (file: File, rateType: string) => void
  onCancel: () => void
  isUploading: boolean
}

const AllowedFiles = '.csv'

export function UploadRfpLanesDialog({
  distanceUnit,
  onUploadConfirm,
  onCancel,
  rateType,
  isUploading,
}: UploadRfpLanesDialogType) {
  const [fileList, setFileList] = useState<Array<FileWithStatus>>([])
  const [selectedRateType, setSelectedRateType] = useState<string>('')
  const isUploadFileDisabled = !selectedRateType || fileList.length !== 1

  function handleOnConfirm() {
    const [{ file }] = fileList

    onUploadConfirm(file, selectedRateType)
  }

  function handleOnFilesAdded(newFiles: File[]) {
    if (newFiles.length === 0) return

    const [file] = newFiles

    setFileList([{ file: file, status: 'success' as const }])
  }

  function handleOnFilesRemoved() {
    setFileList([])
  }

  function handleOnRadioChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectedRateType(event.target.value)
  }

  return (
    <Dialog onOverlayClick={onCancel} open>
      <Dialog.Header>Upload Prices</Dialog.Header>
      <Dialog.Body>
        <Layout.Stack space="l">
          <Banner
            variant="warning"
            title="Please note: Lanes without a rate will not be considered."
          />
          <DragDropFileProvider
            fileList={fileList}
            onFileRemoved={handleOnFilesRemoved}
            onFilesAdded={handleOnFilesAdded}
          >
            <DragDropFile.Wrapper>
              <DragDropFile.DropZone accept={AllowedFiles} />
              <ErrorMessage />
              <DragDropFile.FileList />
            </DragDropFile.Wrapper>
          </DragDropFileProvider>
          <RadioButtonGroup>
            <Radio name="rfp-file-type" value="all_in" onChange={handleOnRadioChanged}>
              <Text>
                Upload as
                <b>{` Flat Rate ${getRateType(rateType)}`}</b>
              </Text>
            </Radio>
            <Radio name="rfp-file-type" value="rpm" onChange={handleOnRadioChanged}>
              <Text>
                Upload as
                <b>{formatDistanceLabel(distanceUnit, getRateType(rateType))}</b>
              </Text>
            </Radio>
          </RadioButtonGroup>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.ActionConfirm
        onConfirm={handleOnConfirm}
        disabled={isUploadFileDisabled}
        loading={isUploading}
      >
        Upload file
      </Dialog.ActionConfirm>
      <Dialog.ActionCancel onCancel={onCancel}>Cancel</Dialog.ActionCancel>
    </Dialog>
  )
}
